import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDay, fmtDate, whatsappLink, gmapsNavUrl, wazeNavUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Truck, MapPin, Camera, CheckCircle2, Upload, Navigation, MessageCircle } from "lucide-react";
import PingMap from "@/components/PingMap";
import UtensilChecklist from "@/components/UtensilChecklist";

const STAGES = [
  "preparing_loading", "loaded", "departed", "arrived_venue",
  "setup_started", "setup_completed", "returning", "returned_warehouse",
];

export default function DeliveryDetailPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [order, setOrder] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoCaption, setPhotoCaption] = useState("");
  const [photoKind, setPhotoKind] = useState("delivery");
  const [liveTracking, setLiveTracking] = useState(false);
  const liveRef = useRef(null);

  const load = async () => {
    const r = await api.get(`/deliveries/${id}`);
    setD(r.data);
    const o = await api.get(`/orders/${r.data.order_id}`);
    setOrder(o.data);
    const v = await api.get("/vehicles");
    setVehicles(v.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Viewers see fresh pings without manual refresh
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/deliveries/${id}`);
        setD((prev) => prev ? { ...prev, gps_pings: r.data.gps_pings, status: r.data.status } : r.data);
      } catch (e) { console.warn("gps refresh failed:", e?.message); }
    }, 30000);
    return () => clearInterval(t);
  }, [id]);

  const advance = async () => {
    const idx = STAGES.indexOf(d.status);
    const next = STAGES[Math.min(idx + 1, STAGES.length - 1)];
    await api.patch(`/deliveries/${id}`, { status: next });
    toast.success(`Status: ${next.replace(/_/g, " ")}`);
    load();
  };

  const toggleItem = async (item, listKey) => {
    const list = (d[listKey] || []).map((x) => x.id === item.id ? { ...x, checked: !x.checked } : x);
    await api.patch(`/deliveries/${id}`, { [listKey]: list });
    load();
  };

  const sendPing = (silent = false) => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      await api.post(`/deliveries/${id}/gps`, { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
      if (!silent) toast.success(`Pinged location (±${Math.round(pos.coords.accuracy)}m)`);
      const r = await api.get(`/deliveries/${id}`);
      setD((prev) => prev ? { ...prev, gps_pings: r.data.gps_pings } : r.data);
    }, () => { if (!silent) toast.error("Could not get location"); });
  };

  const toggleLiveTracking = () => {
    if (liveTracking) {
      clearInterval(liveRef.current);
      liveRef.current = null;
      setLiveTracking(false);
      toast.info("Live tracking stopped");
    } else {
      if (!navigator.geolocation) return toast.error("Geolocation not supported");
      sendPing(true);
      liveRef.current = setInterval(() => sendPing(true), 30000);
      setLiveTracking(true);
      toast.success("Live tracking started — location shared every 30s");
    }
  };
  useEffect(() => () => { if (liveRef.current) clearInterval(liveRef.current); }, []);

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setPhotoFile(r.result);
    r.readAsDataURL(f);
  };
  const upload = async () => {
    await api.post(`/deliveries/${id}/photo`, { data_url: photoFile, caption: photoCaption, kind: photoKind });
    setPhotoFile(null); setPhotoCaption("");
    toast.success("Photo uploaded"); load();
  };
  const confirmCustomer = async () => {
    await api.post(`/deliveries/${id}/confirm-customer`);
    toast.success("Customer confirmation recorded"); load();
  };

  if (!d) return <div className="p-10 text-zinc-500">Loading…</div>;

  const vehicle = vehicles.find((v) => v.id === d.vehicle_id);
  const loadingDone = (d.loading_checklist || []).filter((i) => i.checked).length;
  const loadingTotal = (d.loading_checklist || []).length;
  const lastPing = d.gps_pings?.[d.gps_pings.length - 1];

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="delivery-detail">
      <Link to="/logistics" className="text-sm text-zinc-500 hover:text-[#D4AF37] inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Back</Link>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="label-eyebrow">{order?.event_type}</div>
          <h1 className="font-display text-4xl mt-2">{order?.customer_name}</h1>
          <div className="text-sm text-zinc-500 mt-1 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {order?.event_location}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="stage-chip" style={{ color: "#D4AF37", borderColor: "#D4AF3755" }}>{d.status.replace(/_/g, " ")}</span>
          {d.status !== "returned_warehouse" && (
            <Button onClick={advance} data-testid="advance-delivery" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">Advance →</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Loading checklist */}
          <div className="card-lux p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-eyebrow">Loading Checklist</div>
                <h3 className="font-display text-xl mt-1">Pre-departure inventory</h3>
              </div>
              <div className="text-[#D4AF37] font-display text-xl">{loadingDone}/{loadingTotal}</div>
            </div>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {(d.loading_checklist || []).map((it) => (
                <label key={it.id} className="flex items-center gap-3 p-2 rounded-sm hover:bg-[#0c0c0c] cursor-pointer" data-testid={`loading-${it.id}`}>
                  <input type="checkbox" checked={it.checked} onChange={() => toggleItem(it, "loading_checklist")} className="accent-[#D4AF37] w-4 h-4" />
                  <span className={`flex-1 text-sm ${it.checked ? "line-through text-zinc-600" : "text-zinc-200"}`}>{it.label}</span>
                  <span className="text-xs text-[#D4AF37]">×{it.qty}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Utensil checklist assigned to this order */}
          {d.order_id && <UtensilChecklist orderId={d.order_id} />}

          {/* Photos */}
          <div className="card-lux p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-eyebrow">Delivery Photos</div>
                <h3 className="font-display text-xl mt-1">Proof of work</h3>
              </div>
              <Camera className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div className="p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a] space-y-2 mb-3">
              <div className="flex gap-2 flex-wrap">
                <select value={photoKind} onChange={(e) => setPhotoKind(e.target.value)} className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 h-9 text-sm">
                  <option value="delivery">Delivery</option><option value="setup">Setup</option><option value="completed">Completed</option>
                </select>
                <Input type="file" accept="image/*" onChange={onPhoto} data-testid="d-photo-input" className="bg-[#050505] border-[#2a2a2a] flex-1" />
              </div>
              <Input placeholder="Caption" value={photoCaption} onChange={(e) => setPhotoCaption(e.target.value)} className="bg-[#050505] border-[#2a2a2a]" />
              <Button onClick={upload} disabled={!photoFile} data-testid="d-photo-upload" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B]"><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload</Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(d.photos || []).map((p) => (
                <div key={p.id} className="aspect-square rounded-sm overflow-hidden border border-[#1a1a1a] relative">
                  <img src={p.data_url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] px-2 py-1 truncate">{p.caption || p.kind}</div>
                </div>
              ))}
              {(!d.photos || d.photos.length === 0) && <div className="col-span-full text-center text-zinc-600 text-sm py-4">No photos yet</div>}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Vehicle */}
          <div className="card-lux p-6">
            <div className="label-eyebrow">Vehicle</div>
            <h3 className="font-display text-xl mt-1 mb-3">{vehicle?.plate || "Not assigned"}</h3>
            <div className="text-sm text-zinc-400">{vehicle?.name}</div>
          </div>

          {/* Navigate to venue */}
          {order?.event_location && (
            <div className="card-lux p-6">
              <div className="label-eyebrow">Navigation</div>
              <h3 className="font-display text-xl mt-1 mb-3">Drive to venue</h3>
              <a href={gmapsNavUrl(order)} target="_blank" rel="noreferrer" data-testid="nav-google-maps"
                 className="flex items-center gap-2 px-3 py-2.5 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] text-sm mb-2 transition-colors">
                <MapPin className="w-4 h-4 text-[#4285F4]" /> Open in Google Maps
              </a>
              <a href={wazeNavUrl(order)} target="_blank" rel="noreferrer" data-testid="nav-waze"
                 className="flex items-center gap-2 px-3 py-2.5 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] text-sm transition-colors">
                <Navigation className="w-4 h-4 text-[#33CCFF]" /> Open in Waze
              </a>
            </div>
          )}

          {/* GPS Tracking */}
          <div className="card-lux p-6">
            <div className="label-eyebrow">Live Location</div>
            <h3 className="font-display text-xl mt-1 mb-3">GPS Tracking</h3>
            <PingMap pings={d.gps_pings || []} height={260} />
            <Button onClick={toggleLiveTracking} data-testid="live-tracking-toggle"
              className={`w-full mt-3 ${liveTracking ? "bg-red-600 hover:bg-red-500 text-white" : "bg-[#D4AF37] text-black hover:bg-[#E5C86B]"}`}>
              <Navigation className={`w-3.5 h-3.5 mr-1.5 ${liveTracking ? "animate-pulse" : ""}`} />
              {liveTracking ? "Stop Live Tracking" : "Start Live Tracking"}
            </Button>
            <Button onClick={() => sendPing()} data-testid="gps-ping" variant="outline" className="w-full border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] mt-2">
              <Navigation className="w-3.5 h-3.5 mr-1.5" /> Ping Once
            </Button>
            {liveTracking && <div className="text-[10px] text-emerald-400 mt-2 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Sharing location every 30 seconds — keep this page open</div>}
            <div className="text-xs text-zinc-500 mt-2">Total pings: <span className="text-zinc-300">{(d.gps_pings || []).length}</span></div>
            {lastPing && (
              <div className="mt-2 text-[10px] text-zinc-500 font-mono">
                {lastPing.lat.toFixed(5)}, {lastPing.lng.toFixed(5)} · {fmtDate(lastPing.at)}
              </div>
            )}
          </div>

          {/* Customer confirmation */}
          <div className="card-lux p-6">
            <div className="label-eyebrow">Customer</div>
            <h3 className="font-display text-xl mt-1 mb-3">Confirmation</h3>
            {d.customer_confirmed ? (
              <div className="flex items-center gap-2 text-emerald-500 text-sm"><CheckCircle2 className="w-4 h-4" /> Confirmed {d.customer_confirmed_at && `· ${fmtDate(d.customer_confirmed_at)}`}</div>
            ) : (
              <Button onClick={confirmCustomer} data-testid="confirm-customer" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark as Confirmed
              </Button>
            )}
            {order?.customer_phone && (
              <a href={whatsappLink(order.customer_phone, `Hi ${order.customer_name}, your delivery is currently: ${d.status.replace(/_/g, " ")}.`)} target="_blank" rel="noreferrer"
                 className="mt-2 flex items-center gap-2 px-3 py-2 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] text-sm">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp customer
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
